import { FC } from 'react';
import {
  AutocompleteInput,
  CreateButton,
  DateField,
  EditButton,
  FunctionField,
  ReferenceInput,
  TextField,
  TextInput,
  TopToolbar,
  WrapperField,
} from 'react-admin';

import CraftList from 'src/components/CraftList';
import AuditBasedStatusFilterInput from 'src/inputs/AuditBasedStatusFilterInput';
import ClearFiltersButton from 'src/components/ClearFiltersButton';
import BulkSoftDeleteButton from 'src/components/BulkSoftDeleteButton';
import { usePermissions } from 'src/hooks';
import { ExternalClientRecord } from 'src/types';
import { CraftDateInput } from 'src/components/CraftDateInput';

const columnStyles = {
  actions: {
    width: '12rem',
  },
};

const filters = [
  <TextInput key="nameFilter" source="name" alwaysOn />,
  <ReferenceInput key="orgFilter" reference="organizations" source="org_id" alwaysOn perPage={5}>
    <AutocompleteInput
      label="Organization"
      source="id"
      optionText="name"
      filterToQuery={(q) => {
        if (!Number.isNaN(parseInt(q, 10))) {
          return { id: q };
        }
        return { name: q };
      }}
    />
  </ReferenceInput>,
  <CraftDateInput key="createdAtFrom" label="Created after" source="created_at@_gte" disallowFuture alwaysOn />,
  <CraftDateInput key="createdAtTo" label="Created before" source="created_at@_lte" alwaysOn />,
  <AuditBasedStatusFilterInput key="statusFilter" source="deleted_at@_is_null" inactiveLabel="Deleted" alwaysOn />,
];

const ListActions = ({ canCreate }: { canCreate: boolean }) => (
  <TopToolbar>
    <ClearFiltersButton />
    {canCreate && <CreateButton />}
  </TopToolbar>
);

export const ExternalClientsList: FC = () => {
  const { permissions } = usePermissions();

  const canCreate = permissions.has('externalClient:edit');
  const canEdit = permissions.has('externalClient:edit');
  const canDelete = permissions.has('externalClient:delete');

  const bulkActionButtons = canDelete && (
    <BulkSoftDeleteButton
      label="Delete Selected"
      dialogTitle="Delete External Clients (%{smart_count})"
      dialogContent="Are you sure you want to delete these items?"
      data={{ deleted_at: 'now()' }}
    />
  );

  return (
    <CraftList
      title="External Clients"
      filters={filters}
      filterDefaultValues={{ 'deleted_at@_is_null': true }}
      actions={<ListActions canCreate={canCreate} />}
      showClearButton={false}
      sort={{ field: 'created_at', order: 'DESC' }}
      dataGridProps={{ bulkActionButtons }}
      columnStyles={columnStyles}
    >
      <TextField source="org_id" label="Org ID" />
      <TextField source="name" />
      <TextField source="id" label="ID" />
      <DateField source="created_at" showTime />
      <WrapperField label="Actions" headerClassName="column-actions" cellClassName="column-actions">
        {canEdit && (
          <FunctionField render={(rec: ExternalClientRecord) => <EditButton disabled={!!rec.deleted_at} />} />
        )}
      </WrapperField>
    </CraftList>
  );
};

export default ExternalClientsList;
